import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Eye, Link2, Moon, Skull, Wind, BoxIcon } from "lucide-react";
import { features, productDetails, productShots } from "../../data/blackout";
import { scrollToSection } from "../../utils/scroll";
import { FeatureIcon } from "../../types/blackout";
const featureIcons: Record<FeatureIcon, BoxIcon> = {
  skull: Skull,
  eyes: Eye,
  vents: Wind,
  strap: Link2,
  finish: Moon
};
export function MaskSection() {
  const [activeId, setActiveId] = useState(productShots[0].id);
  const active = productShots.find(s => s.id === activeId) ?? productShots[0];
  return <section id="mask" className="relative bg-carbon py-20 md:py-28" data-id="element-221" data-mp="2x7x9rivae4oe">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 md:px-8 lg:grid-cols-12 lg:gap-14" data-id="element-222" data-mp="325p1abs0ros2">
        {/* Gallery + detail crops — real product photography only */}
        <div className="lg:order-2 lg:col-span-7" data-id="element-223" data-mp="heg8wfx1o61a">
          <div className="relative aspect-square overflow-hidden border border-gunmetal bg-obsidian md:aspect-[5/4]" data-id="element-224" data-mp="hcvb8hffo6z7">
            <AnimatePresence initial={false} data-id="element-225" data-mp="1ef5c08i8cpg7">
              <motion.img key={active.id} src={active.image} alt={active.alt} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="absolute inset-0 h-full w-full object-cover" data-id="element-226" data-mp="19i6fjjb3g6eu" />
            </AnimatePresence>
            <span className="absolute right-3 top-3 border border-bone/40 bg-obsidian/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-bone backdrop-blur-sm" data-id="element-227" data-mp="qqvuhis1unuv">
              Real product
            </span>
            <div role="tablist" aria-label="Product views" className="absolute bottom-3 left-3 flex gap-1 bg-obsidian/80 p-1 backdrop-blur-sm" data-id="element-228" data-mp="ht3f6embvg7h">
              {productShots.map(shot => {
              const selected = shot.id === activeId;
              return <button key={shot.id} type="button" role="tab" aria-selected={selected} onClick={() => setActiveId(shot.id)} className={`h-8 whitespace-nowrap px-3 text-[11px] font-medium uppercase tracking-[0.12em] transition-colors duration-150 ${selected ? 'bg-bone text-obsidian' : 'text-bone/75 hover:text-bone'}`} data-id="element-229" data-mp="3uabi5q3769g4">
                    {shot.label}
                  </button>;
            })}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-4 gap-2 md:gap-3" data-id="element-230" data-mp="gx9h5408whun">
            {productDetails.map(detail => <figure key={detail.id} data-id="element-231" data-mp="ltzwfp4879f0">
                <div className="relative aspect-square overflow-hidden border border-gunmetal bg-obsidian" data-id="element-232" data-mp="lx4b5gfbhnjx">
                  <img src={detail.image} alt={`Close-up: ${detail.label}`} loading="lazy" style={{
                transform: `scale(${detail.zoom})`,
                transformOrigin: detail.origin
              }} className="h-full w-full object-cover" data-id="element-233" data-mp="74apfd9gcb6t" />
                </div>
                <figcaption className="mt-2 text-[10px] font-medium uppercase leading-tight tracking-[0.12em] text-steel md:text-[11px]" data-id="element-234" data-mp="14mu2ftjvfr73">
                  {detail.label}
                </figcaption>
              </figure>)}
          </div>
        </div>

        {/* Copy + features */}
        <div className="lg:order-1 lg:col-span-5" data-id="element-235" data-mp="2z17wbgeaao7c">
          <p className="text-[12px] font-medium uppercase tracking-[0.32em] text-steel" data-id="element-236" data-mp="2u2wofb7junrr">
            BU-01 // Ultimate Mask
          </p>
          <h2 className="mt-3 font-display text-5xl uppercase leading-[0.95] text-bone md:text-6xl" data-id="element-237" data-mp="26dv8z40auccz">
            The mask that changes the whole fit.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-bone/80 md:text-lg" data-id="element-238" data-mp="2fdi9lnl7fe4z">
            Macabre, dark and anonymous. Throw it on with a black hoodie and
            you’re someone else.
          </p>

          <ul className="mt-10 border-t border-gunmetal" data-id="element-239" data-mp="3qs5modt6hwpn">
            {features.map(feature => {
            const Icon = featureIcons[feature.icon];
            return <li key={feature.title} className="flex items-center gap-4 border-b border-gunmetal py-4" data-id="element-240" data-mp="3mtodloq76c5l">
                  <Icon className="h-5 w-5 shrink-0 text-bone" strokeWidth={1.5} aria-hidden="true" data-id="element-241" data-mp="pxpsc7jy29ge" />
                  <div className="min-w-0" data-id="element-242" data-mp="uwsmuy2asxa4">
                    <h3 className="font-sans text-[14px] font-semibold uppercase tracking-[0.06em] text-bone" data-id="element-243" data-mp="uw12kje5ilz5">
                      {feature.title}
                    </h3>
                    <p className="mt-0.5 text-sm text-steel" data-id="element-244" data-mp="14vi72xho2ttj">{feature.description}</p>
                  </div>
                </li>;
          })}
          </ul>

          <p className="mt-5 text-sm text-steel" data-id="element-245" data-mp="18uh7q5wd84f6">
            <span className="font-semibold text-bone" data-id="element-246" data-mp="1dn0lp3yyt1ki">Standard:</span> two
            smoke-black eye crystals, installed.
          </p>

          <button type="button" onClick={() => scrollToSection('bundles')} className="group mt-8 inline-flex h-14 w-full items-center justify-center gap-3 bg-bone px-8 text-[13px] font-semibold uppercase tracking-[0.2em] text-obsidian transition-[background-color,transform] duration-150 ease-out hover:bg-white active:scale-[0.98] sm:w-auto" data-id="element-247" data-mp="1illlj2ab1j8f">
            Choose your chapter
            <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-248" data-mp="382piypiwv4bc" />
          </button>
        </div>
      </div>
    </section>;
}